/* eslint-disable @typescript-eslint/member-ordering */
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetStyleProps } from '@finxone-platform/shared/sys-config-types';

@Component({
  standalone: false,
  selector: 'finx-dropdown',
  templateUrl: './finx-dropdown.component.html',
  styleUrls: ['./finx-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxDropdownComponent),
      multi: true,
    },
  ],
})
export class FinxDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  private cd = inject(ChangeDetectorRef);

  @ViewChild('dropdownBoxRef') dropdownBoxRef: ElementRef<HTMLElement>;
  @ViewChild('dropdownPanelRef') dropdownPanelRef: ElementRef<HTMLElement>;

  @Input() class = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() options: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() value: any = '';
  // No longer used since the panel is no longer appended to <body> (kept for API back-compat).
  @Input() appendto = '';
  @Input() error = '';
  @Input() placeholder = '';
  @Input() id = '';
  @Input() label = '';
  @Input() isDisabled = false;
  @Input() filter = false;
  @Input() filterBy = '';
  @Input() showAccountsListWithFlag = false;
  @Input() showAccountsListForExchange = false;
  @Input() placeholderAsLabel?: boolean;
  @Input() public colorTextAlignStyle: WidgetStyleProps;
  @Input() public textInputFontStyle: WidgetStyleProps;
  @Input() public textInputBackgroundColor: WidgetStyleProps;
  @Input() public inputStyle: WidgetStyleProps = {};
  @Input() public panelStyleClass: string;
  @Output() valueChange = new EventEmitter<string>();
  @Output() errorChange = new EventEmitter<string>();
  @Output() focusEvent = new EventEmitter<boolean>();
  public isFocused = false;
  public readonly dropdownStringControl = new FormControl();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('selectedItemTemplate') selectedItemTemplate: TemplateRef<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('itemTemplate') itemTemplate: TemplateRef<any>;

  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelChange: Function = () => {};

  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelTouched: Function = () => {};
  @Input() textColor: WidgetStyleProps;
  public textCombinedStyle: WidgetStyleProps;

  public isOpen = false;
  public searchQuery = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public filteredOptions: any[] = [];

  // Inline style applied to the dropdown panel so it can escape any ancestor
  // container that has a fixed/small height and would otherwise clip it
  // (e.g. overflow: hidden on a shorter wrapping div).
  public panelPositionStyle: { [key: string]: string } = {};

  private readonly onWindowChange = () => this.updatePanelPosition();

  ngOnDestroy(): void {
    this.removeRepositionListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateTextCombinedStyle();
    if (changes['options']) {
      this.computeFilteredOptions();
    }
  }
  ngOnInit(): void {
    this.dropdownStringControl.valueChanges.subscribe((dateString) => {
      this.onModelChange(dateString);
      this.onModelTouched();
      this.cd.detectChanges();
    });
    this.updateTextCombinedStyle();
    this.computeFilteredOptions();
  }
  private updateTextCombinedStyle(): void {
    this.textCombinedStyle = {
      ...this.textColor,
      ...this.textInputFontStyle,
      ...this.textInputBackgroundColor,
      ...this.inputStyle,
    };
  }

  writeValue(value: Date | null): void {
    value = value ?? new Date();

    this.dropdownStringControl.setValue(value);
    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.dropdownStringControl.disable();
    } else {
      this.dropdownStringControl.enable();
    }
    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange = (event: any) => {
    this.value = event.value;
    this.valueChange.emit(this.value ?? '');
    this.error = '';
    this.errorChange.emit('');
  };

  onNgModelChange = () => {
    this.error = '';
    this.errorChange.emit('');
  };
  onFocus = () => {
    this.isFocused = !this.isFocused;
    this.focusEvent.emit(this.isFocused);
  };
  onBlurEvent = () => {
    this.isFocused = !this.isFocused;
    this.focusEvent.emit(this.isFocused);
  };

  public toggleDropdown(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.computeFilteredOptions();
      this.onFocus();
      this.updatePanelPosition();
      this.addRepositionListeners();
      // Recompute once the panel has actually rendered so we can account for
      // its real height when deciding whether to flip it above the trigger.
      requestAnimationFrame(() => this.updatePanelPosition());
    } else {
      this.searchQuery = '';
      this.removeRepositionListeners();
    }
  }

  public closeDropdown(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchQuery = '';
    this.removeRepositionListeners();
  }

  private addRepositionListeners(): void {
    window.addEventListener('scroll', this.onWindowChange, true);
    window.addEventListener('resize', this.onWindowChange, true);
  }

  private removeRepositionListeners(): void {
    window.removeEventListener('scroll', this.onWindowChange, true);
    window.removeEventListener('resize', this.onWindowChange, true);
  }

  private updatePanelPosition(): void {
    if (!this.isOpen || !this.dropdownBoxRef?.nativeElement) return;

    const boxRect = this.dropdownBoxRef.nativeElement.getBoundingClientRect();
    const panelHeight = this.dropdownPanelRef?.nativeElement?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - boxRect.bottom;
    const spaceAbove = boxRect.top;
    const shouldFlipUp = panelHeight > 0 && spaceBelow < panelHeight && spaceAbove > spaceBelow;

    this.panelPositionStyle = {
      position: 'fixed',
      left: `${boxRect.left}px`,
      width: `${boxRect.width}px`,
      ...(shouldFlipUp
        ? { bottom: `${window.innerHeight - boxRect.top + 2}px` }
        : { top: `${boxRect.bottom + 2}px` }),
    };
    this.cd.markForCheck();
  }

  // The showAccountsListWithFlag variant renders an in-panel search input when filtering is on;
  // wiring blur there would fire as soon as that input takes focus, so it's intentionally skipped here too.
  public onBoxBlur(): void {
    if (!this.showAccountsListWithFlag) {
      this.onBlurEvent();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectOption(option: any): void {
    const val = this.getOptionValue(option);
    this.dropdownStringControl.setValue(val);
    this.onChange({ value: val });
    this.closeDropdown();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getOptionValue(option: any): any {
    return this.id ? option?.[this.id] : option;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getOptionLabel(option: any): any {
    return this.label ? option?.[this.label] : option;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isSelected(option: any): boolean {
    return this.getOptionValue(option) === this.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get selectedDisplayOption(): any {
    return this.id ? this.options?.find((option) => option?.[this.id] === this.value) : this.value;
  }

  public onSearchInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.computeFilteredOptions();
    this.cd.markForCheck();
  }

  private computeFilteredOptions(): void {
    if (!this.searchQuery) {
      this.filteredOptions = this.options ?? [];
      return;
    }
    const query = this.searchQuery.toLowerCase();
    this.filteredOptions = (this.options ?? []).filter((option) => {
      const text = this.filterBy ? option?.[this.filterBy] : option;
      return String(text ?? '')
        .toLowerCase()
        .includes(query);
    });
  }
}
